import React from 'react';
import { Accessibility, Check, Mail } from 'lucide-react';

const AccessibilityStatementPage = ({ onBack }) => {
  const features = [
    'Semantic HTML structure for screen reader compatibility',
    'Keyboard navigation support throughout the application',
    'Skip to main content links for easier navigation',
    'ARIA labels and descriptions on interactive elements',
    'High contrast color combinations for text readability',
    'Focus indicators on all interactive elements',
    'Alt text on all meaningful images',
    'Responsive design that works on all device sizes',
    'Form labels and error messages for accessibility',
    'Logical heading hierarchy'
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="mb-6 text-blue-600 hover:text-blue-800 flex items-center gap-2"
        >
          ← Back to Home
        </button>

        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <Accessibility className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Accessibility Statement</h1>
          </div>

          {/* Commitment */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Our Commitment</h2>
            <p className="text-gray-700 leading-relaxed">
              AbleFam is committed to ensuring digital accessibility for people with disabilities. 
              We are continually improving the user experience for everyone and applying the relevant 
              accessibility standards to ensure we provide equal access to all users.
            </p>
          </section>

          {/* Conformance Status */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Conformance Status</h2>
            <p className="text-gray-700 leading-relaxed">
              We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA. 
              These guidelines explain how to make web content more accessible for people with disabilities 
              and more user-friendly for everyone.
            </p>
          </section>

          {/* Accessibility Features */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Accessibility Features</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              AbleFam includes the following accessibility features:
            </p>
            <ul className="space-y-2">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Assistive Technologies */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Assistive Technology Compatibility</h2>
            <p className="text-gray-700 leading-relaxed">
              AbleFam is designed to be compatible with the following assistive technologies:
            </p>
            <ul className="mt-4 space-y-2 text-gray-700">
              <li>• Screen readers (including NVDA, JAWS, and VoiceOver)</li>
              <li>• Screen magnification software</li>
              <li>• Speech recognition software</li>
              <li>• Keyboard-only navigation</li>
            </ul>
          </section>

          {/* Known Limitations */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Known Limitations</h2>
            <p className="text-gray-700 leading-relaxed">
              While we strive to make AbleFam as accessible as possible, some user-generated content 
              (such as community-submitted venue descriptions or reviews) may not always meet 
              accessibility standards. We encourage our community to use clear, descriptive language 
              when contributing content.
            </p>
          </section>

          {/* Feedback */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Feedback</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We welcome your feedback on the accessibility of AbleFam. If you encounter any 
              accessibility barriers or have suggestions for improvement, please let us know.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
              <Mail className="w-5 h-5 text-blue-600" />
              <span className="text-gray-700">
                Contact us through our <button onClick={onBack} className="text-blue-600 hover:underline">Contact Page</button> to report accessibility issues.
              </span>
            </div>
          </section>

          {/* Last Updated */}
          <div className="text-sm text-gray-500 pt-6 border-t border-gray-200">
            Last updated: January 2025
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityStatementPage;
